<template>
  <div class="upcoming-draws upcoming-draws-view">
    <BetBuilderPanel v-if="!isLite" />

    <LotterySection
      v-if="!isLite"
      :title="featuredSectionTitle"
      :lotteries="featuredDraws"
      :is-favorited="isFavorited"
      :show-filters="true"
      layout="vertical"
      @bet="handleBet"
      @favorite="handleFavorite"
      @removeLottery="removeFeaturedLottery"
    >
      <template #header>
        <SectionHeader
          :title="featuredSectionTitle"
          variant="simple"
        />
      </template>
    </LotterySection>

    <FavoritesSection
      v-if="!isLite"
      :favorites="favorites"
      :is-favorited="isFavorited"
      @favorite="handleFavorite"
      @remove="removeExpiredDraw"
    >
      <template #header>
        <SectionHeader
          :title="$t('sections.favourites')"
          variant="simple"
        />
      </template>
    </FavoritesSection>

    <MostPlayedSection
      v-if="!isLite"
      :most-played="mostPlayed"
      :is-favorited="isFavorited"
      @favorite="handleFavorite"
      @remove="removeExpiredDraw"
    >
      <template #header>
        <SectionHeader
          :title="$t('sections.mostPlayed')"
          variant="simple"
        />
      </template>
    </MostPlayedSection>

    <div class="lucky-numbers-section">
      <LuckyNumbersSection
        :title="$t('sections.luckyNumbers')"
        :games="upcomingDrawsStore.draws"
        :is-favorited="isFavorited"
        title-align="left"
        :date-filter-only="false"
        @bet="handleBet"
        @favorite="handleFavorite"
        @remove="removeExpiredDraw"
      >
        <template #header>
          <SectionHeader
            :title="$t('sections.luckyNumbers')"
            variant="simple"
          />
        </template>
      </LuckyNumbersSection>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
// ... (rest of imports remain same)
import LotterySection from '@/components/LotterySection.vue'
import FavoritesSection from "@/components/FavoritesSection.vue";
import MostPlayedSection from "@/components/MostPlayedSection.vue";
import LuckyNumbersSection from "@/components/LuckyNumbersSection.vue";
import BetBuilderPanel from "@/components/BetBuilderPanel.vue";
import SectionHeader from "@/components/base/SectionHeader.vue";
import { useUpcomingDrawsStore } from "@/stores/upcomingDraws";
import { useGameStore } from "@/stores/game";
import type { UpcomingDraw } from "@/types/lottery";
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from "@/stores/auth";
import { useI18n } from "vue-i18n";
import { useIntegrationMode } from '@/composables/useIntegrationMode'

const { t } = useI18n();
const { isLite } = useIntegrationMode()
const authStore = useAuthStore()
const gameStore = useGameStore();
const router = useRouter();
const route = useRoute();

const favorites = ref<UpcomingDraw[]>([]);
const mostPlayed = ref<UpcomingDraw[]>([]);
const upcomingDrawsStore = useUpcomingDrawsStore();

const usePopularDrawsSection = computed(
  () => gameStore.getBetSettings?.client_surface_ui?.use_popular_draws_section === true
);

const featuredSectionTitle = computed(() =>
  usePopularDrawsSection.value ? t('sections.popularDraws') : t('sections.upcomingDraws')
);

const featuredDraws = computed(() =>
  usePopularDrawsSection.value ? upcomingDrawsStore.featuredDraws : upcomingDrawsStore.draws
);

// Load favorites from local storage
const loadFavorites = () => {
  const savedFavorites = localStorage.getItem('favoriteDraws');
  if (savedFavorites) {
    const cachedFavorites = JSON.parse(savedFavorites);
    favorites.value = syncWithFreshData(cachedFavorites);
  }
};

const syncWithFreshData = (cachedData: UpcomingDraw[]) => {
  return cachedData.map(cachedItem => {
    const freshData = upcomingDrawsStore.draws.find(draw => draw.game_slug === cachedItem.game_slug);
    if (freshData) {
      return {
        ...cachedItem,
        draw_date: freshData.draw_date,
        cutoff: freshData.cutoff,
        game_name: freshData.game_name,
        game_logo: freshData.game_logo,
      };
    }
    return cachedItem;
  });
};

// Save favorites to local storage
const saveFavorites = () => {
  localStorage.setItem('favoriteDraws', JSON.stringify(favorites.value));
};

// Load most played from local storage and sync with fresh data
const loadMostPlayed = () => {
  const savedMostPlayed = localStorage.getItem('mostPlayedDraws');
  if (savedMostPlayed) {
    const mostPlayedData = JSON.parse(savedMostPlayed);
    // Sync with fresh data and sort by play count, take top 5
    const syncedData = syncWithFreshData(mostPlayedData);
    mostPlayed.value = syncedData
      .sort((a: UpcomingDraw & { playCount: number }, b: UpcomingDraw & { playCount: number }) => b.playCount - a.playCount)
      .slice(0, 5);
  }
};

// Save most played to local storage
const saveMostPlayed = (draw: UpcomingDraw) => {
  const savedMostPlayed = localStorage.getItem('mostPlayedDraws');
  let mostPlayedData = savedMostPlayed ? JSON.parse(savedMostPlayed) : [];
  
  // Find if draw already exists in most played
  const existingIndex = mostPlayedData.findIndex((item: UpcomingDraw & { playCount: number }) => item.id === draw.id);
  
  if (existingIndex !== -1) {
    // Increment play count if draw exists
    mostPlayedData[existingIndex].playCount++;
  } else {
    // Add new draw with play count 1
    mostPlayedData.push({ ...draw, playCount: 1 });
  }
  
  // Sort by play count and take top 5
  mostPlayedData = mostPlayedData
    .sort((a: UpcomingDraw & { playCount: number }, b: UpcomingDraw & { playCount: number }) => b.playCount - a.playCount)
    .slice(0, 5);
  
  // Save to local storage
  localStorage.setItem('mostPlayedDraws', JSON.stringify(mostPlayedData));
  
  // Update mostPlayed ref
  mostPlayed.value = mostPlayedData;
};

const handleBet = (draw: UpcomingDraw) => {
  saveMostPlayed(draw);
  router.push({ path: `/lottery/${draw.game_slug}`, query: route.query });
};

const handleFavorite = (draw: UpcomingDraw) => {
  if (!draw || typeof draw.id === 'undefined') return;
  const index = favorites.value.findIndex(fav => fav.id === draw.id);
  if (index === -1) {
    favorites.value.push(draw);
  } else {
    favorites.value.splice(index, 1);
  }
  saveFavorites();
};

const isFavorited = (draw: UpcomingDraw) => {
  if (!draw || typeof draw.id === 'undefined') return false;
  return favorites.value.some(fav => fav.id === draw.id);
};

const removeLottery = (lotteryToRemove: UpcomingDraw) => {
  const idx = upcomingDrawsStore.draws.findIndex(l => l.id === lotteryToRemove.id);
  if (idx !== -1) {
    upcomingDrawsStore.draws.splice(idx, 1);
    if (upcomingDrawsStore.draws.length === 0) {
      upcomingDrawsStore.fetchDraws(1);
    }
  }
};

const removeExpiredDraw = (lotteryToRemove: UpcomingDraw) => {
  removeLottery(lotteryToRemove);

  const favIdx = favorites.value.findIndex((f) => f.id === lotteryToRemove.id);
  if (favIdx !== -1) {
    favorites.value.splice(favIdx, 1);
    saveFavorites();
  }

  const playedIdx = mostPlayed.value.findIndex((m) => m.id === lotteryToRemove.id);
  if (playedIdx !== -1) {
    mostPlayed.value.splice(playedIdx, 1);
    const savedMostPlayed = localStorage.getItem('mostPlayedDraws');
    if (savedMostPlayed) {
      const trimmed = JSON.parse(savedMostPlayed).filter(
        (item: UpcomingDraw) => item.id !== lotteryToRemove.id
      );
      localStorage.setItem('mostPlayedDraws', JSON.stringify(trimmed));
    }
  }
};

const removeFeaturedLottery = (lotteryToRemove: UpcomingDraw) => {
  if (usePopularDrawsSection.value) {
    const idx = upcomingDrawsStore.featuredDraws.findIndex(l => l.id === lotteryToRemove.id);
    if (idx !== -1) {
      upcomingDrawsStore.featuredDraws.splice(idx, 1);
      if (upcomingDrawsStore.featuredDraws.length === 0) {
        upcomingDrawsStore.fetchFeaturedDraws(1);
      }
    }
    return;
  }
  removeLottery(lotteryToRemove);
};

const loadFeaturedDraws = async () => {
  if (usePopularDrawsSection.value) {
    await upcomingDrawsStore.fetchFeaturedDraws(1);
  }
};

watch(
  () => upcomingDrawsStore.draws,
  () => {
    if (upcomingDrawsStore.draws.length > 0) {
      loadFavorites();
      loadMostPlayed();
    }
  },
  { deep: true }
);

onMounted(async () => {
  authStore.refreshAuthFromExternalSources();

  loadFavorites();
  loadMostPlayed();
  await upcomingDrawsStore.fetchDraws(1);
  if (!isLite.value) {
    await loadFeaturedDraws();
  }
});

watch(usePopularDrawsSection, async (enabled) => {
  if (enabled) {
    await upcomingDrawsStore.fetchFeaturedDraws(1);
  }
});
</script>

<style scoped>
.upcoming-draws-view {
  padding: 5px;
}

.section {
  margin-bottom: 8px;
}

/* Custom styles specific to UpcomingDrawsView */

.scroll-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--bg-dark-tertiary);
  border: 1px solid var(--colour-orange-button);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--colour-icons);
  z-index: 10;
  transition: all 0.2s;
}

.scroll-btn:hover {
  background: var(--colour-surface-cont);
  border-color: var(--colour-orange-button);
}

.scroll-left {
  left: -12px;
}

.scroll-right {
  right: -12px;
}

@media screen and (max-width: 768px) {
  .upcoming-draws-view {
    padding: 10px;
  }
}
</style>
